import { DestroyRef, Injectable } from '@angular/core';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';

/**
 * Simple service for setting up reactive watchers on checkbox UI layer visibility changes.
 * Handles both flat collections and recursive hierarchies with proper cleanup.
 */
@Injectable({
  providedIn: 'root'
})
export class ReactiveCheckboxWatcherService {
  /**
   * Sets up reactive watchers for checkbox-controlled layers with recursive support.
   *
   * @param items - Array, collection, or iterable of items to watch
   * @param getVisible - Function to extract the visible property from an item
   * @param getChildren - Function to extract children from an item (for recursion)
   * @param onVisibilityChange - Callback when any item's visibility changes
   * @param destroyRef - DestroyRef for automatic cleanup
   */
  setupWatchers<T>(
    items: T[] | Iterable<T> | any, // Accept any collection type including ArcGIS Collections
    getVisible: (item: T) => boolean | undefined,
    getChildren: (item: T) => T[] | Iterable<T> | any | undefined,
    onVisibilityChange: () => void,
    destroyRef: DestroyRef
  ): void {
    // Convert any collection type to array
    let itemsArray: T[];
    if (Array.isArray(items)) {
      itemsArray = items;
    } else if (items && typeof items[Symbol.iterator] === 'function') {
      itemsArray = Array.from(items);
    } else if (items && typeof items.forEach === 'function') {
      // Handle ArcGIS Collections that have forEach but may not be iterable
      itemsArray = [];
      items.forEach((item: T) => itemsArray.push(item));
    } else {
      return; // Can't process this type
    }
    itemsArray.forEach((item) => {
      // Set up watcher for this item if it has a visible property
      if (getVisible(item) !== undefined) {
        const watcher = reactiveUtils.watch(
          () => getVisible(item),
          () => onVisibilityChange()
        );
        destroyRef.onDestroy(() => watcher.remove());
      }

      // Recursively set up watchers for children
      const children = getChildren(item);
      if (children) {
        this.setupWatchers(
          children,
          getVisible,
          getChildren,
          onVisibilityChange,
          destroyRef
        );
      }
    });
  }
}
